import { Layer } from 'mapbox-gl';

import { SymbolLayerWithMapIcon } from '../components/MapIcon';
import { FishbrainLayer, FishbrainLayers, LayerWithToggleableGroup } from '../types/types';

import {
  ARTIFICIAL_REEFS_HIGHLIGHT_LAYER,
  ARTIFICIAL_REEFS_LAYER,
  ARTIFICIAL_REEFS_SIMPLIFIED_HIGHLIGHT_LAYER,
  ARTIFICIAL_REEFS_SIMPLIFIED_LAYER,
} from './mapLayers/artificialReefs';
import {
  CATCH_POSITIONS_HIGHLIGHT_LAYER,
  CATCH_POSITIONS_LAYER,
  CATCH_POSITIONS_SIMPLIFIED_HIGHLIGHT_LAYER,
  CATCH_POSITIONS_SIMPLIFIED_LAYER,
} from './mapLayers/catchPositions';
import {
  CATCH_COUNTS_LAYER,
  EMPTY_FISHING_WATERS_LAYER,
  EMPTY_FISHING_WATERS_SIMPLIFIED_LAYER,
  FISHING_WATERS_HIGHLIGHT_LAYER,
  FISHING_WATERS_LAYER,
  FISHING_WATERS_NEW_LAYER,
  FISHING_WATERS_SIMPLIFIED_LAYER,
  FORBIDDEN_FISHING_WATERS_LAYER,
  FORBIDDEN_FISHING_WATERS_SIMPLIFIED_LAYER,
} from './mapLayers/fishingWaters';
import {
  POINTS_OF_INTEREST_LAYERS,
  POINTS_OF_INTEREST_HIGHLIGHT_LAYER,
  POINTS_OF_INTEREST_SIMPLIFIED_LAYER,
} from './mapLayers/pointsOfInterest';
import { SPOT_PREDICTION_LAYER } from './mapLayers/spotPrediction';
import {
  WAYPOINTS_LAYERS,
  WAYPOINTS_HIGHLIGHT_LAYER,
  WAYPOINTS_SIMPLIFIED_LAYER,
  WAYPOINTS_SIMPLIFIED_HIGHLIGHT_LAYER,
} from './mapLayers/waypoints';
import { SOURCE } from './sources';

export interface MapboxLayer {
  mapboxLayers: (LayerWithToggleableGroup & (Layer | SymbolLayerWithMapIcon))[];
  sourceName: string;
}

export const MAPBOX_LAYERS: FishbrainLayers<MapboxLayer> = {
  catchPositions: {
    mapboxLayers: [
      // defines also order within this group; highest priority on bottom
      CATCH_POSITIONS_SIMPLIFIED_HIGHLIGHT_LAYER,
      CATCH_POSITIONS_HIGHLIGHT_LAYER,
      CATCH_POSITIONS_SIMPLIFIED_LAYER,
      CATCH_POSITIONS_LAYER,
    ],
    sourceName: SOURCE.catchPositions.name,
  },
  fishingWaters: {
    mapboxLayers: [
      // defines also order within this group; highest priority on bottom
      FISHING_WATERS_HIGHLIGHT_LAYER,
      FORBIDDEN_FISHING_WATERS_SIMPLIFIED_LAYER,
      FORBIDDEN_FISHING_WATERS_LAYER,
      EMPTY_FISHING_WATERS_SIMPLIFIED_LAYER,
      FISHING_WATERS_SIMPLIFIED_LAYER,
      EMPTY_FISHING_WATERS_LAYER,
      FISHING_WATERS_LAYER,
      FISHING_WATERS_NEW_LAYER,
      CATCH_COUNTS_LAYER,
    ],
    sourceName: SOURCE.fishingWaters.name,
  },
  pointsOfInterest: {
    mapboxLayers: [
      // defines also order within this group; highest priority on bottom
      POINTS_OF_INTEREST_HIGHLIGHT_LAYER,
      POINTS_OF_INTEREST_SIMPLIFIED_LAYER.RAMP,
      POINTS_OF_INTEREST_SIMPLIFIED_LAYER.MARINA,
      POINTS_OF_INTEREST_SIMPLIFIED_LAYER.BAIT_SHOP,
      POINTS_OF_INTEREST_SIMPLIFIED_LAYER.LICENSE_VENDOR,
      POINTS_OF_INTEREST_SIMPLIFIED_LAYER.CHARTER,
      POINTS_OF_INTEREST_SIMPLIFIED_LAYER.GEAR_AND_EQUIPMENT,
      POINTS_OF_INTEREST_SIMPLIFIED_LAYER.FLY_FISHING_SHOP,
      POINTS_OF_INTEREST_SIMPLIFIED_LAYER.REFUGE,
      POINTS_OF_INTEREST_SIMPLIFIED_LAYER.NATIONAL_PARK,
      POINTS_OF_INTEREST_LAYERS.RAMP,
      POINTS_OF_INTEREST_LAYERS.MARINA,
      POINTS_OF_INTEREST_LAYERS.BAIT_SHOP,
      POINTS_OF_INTEREST_LAYERS.LICENSE_VENDOR,
      POINTS_OF_INTEREST_LAYERS.CHARTER,
      POINTS_OF_INTEREST_LAYERS.GEAR_AND_EQUIPMENT,
      POINTS_OF_INTEREST_LAYERS.FLY_FISHING_SHOP,
      POINTS_OF_INTEREST_LAYERS.REFUGE,
      POINTS_OF_INTEREST_LAYERS.NATIONAL_PARK,
    ],
    sourceName: SOURCE.pointsOfInterest.name,
  },
  artificialReefs: {
    mapboxLayers: [
      ARTIFICIAL_REEFS_SIMPLIFIED_HIGHLIGHT_LAYER,
      ARTIFICIAL_REEFS_HIGHLIGHT_LAYER,
      ARTIFICIAL_REEFS_SIMPLIFIED_LAYER,
      ARTIFICIAL_REEFS_LAYER,
    ],
    sourceName: SOURCE.artificialReefs.name,
  },
  spotPrediction: {
    mapboxLayers: [SPOT_PREDICTION_LAYER],
    sourceName: SOURCE.spotPrediction.name,
  },
  waypoints: {
    mapboxLayers: [
      // defines also order within this group; highest priority on bottom
      WAYPOINTS_SIMPLIFIED_HIGHLIGHT_LAYER,
      WAYPOINTS_HIGHLIGHT_LAYER,
      WAYPOINTS_SIMPLIFIED_LAYER.WRECK,
      WAYPOINTS_SIMPLIFIED_LAYER.WARNING,
      WAYPOINTS_SIMPLIFIED_LAYER.VILE_AREA,
      WAYPOINTS_SIMPLIFIED_LAYER.VEGETATION,
      WAYPOINTS_SIMPLIFIED_LAYER.SUNKEN_TREE,
      WAYPOINTS_SIMPLIFIED_LAYER.STRUCTURE,
      WAYPOINTS_SIMPLIFIED_LAYER.SLIPWAY,
      WAYPOINTS_SIMPLIFIED_LAYER.SHELTER,
      WAYPOINTS_SIMPLIFIED_LAYER.SCHOOL_OF_FISH,
      WAYPOINTS_SIMPLIFIED_LAYER.ROCKS,
      WAYPOINTS_SIMPLIFIED_LAYER.PICNIC,
      WAYPOINTS_SIMPLIFIED_LAYER.PHOTO,
      WAYPOINTS_SIMPLIFIED_LAYER.PARKING,
      WAYPOINTS_SIMPLIFIED_LAYER.NOTE,
      WAYPOINTS_SIMPLIFIED_LAYER.NOPE,
      WAYPOINTS_SIMPLIFIED_LAYER.LOST_LURE,
      WAYPOINTS_SIMPLIFIED_LAYER.LOST_FISH,
      WAYPOINTS_SIMPLIFIED_LAYER.LILY_PADS,
      WAYPOINTS_SIMPLIFIED_LAYER.GAS,
      WAYPOINTS_SIMPLIFIED_LAYER.FISH_SPOTTED,
      WAYPOINTS_SIMPLIFIED_LAYER.FAVORITE,
      WAYPOINTS_SIMPLIFIED_LAYER.ELECTRICITY,
      WAYPOINTS_SIMPLIFIED_LAYER.DEFAULT,
      WAYPOINTS_SIMPLIFIED_LAYER.CAMPFIRE,
      WAYPOINTS_SIMPLIFIED_LAYER.ANCHOR_SPOT,
      WAYPOINTS_LAYERS.WRECK,
      WAYPOINTS_LAYERS.WARNING,
      WAYPOINTS_LAYERS.VILE_AREA,
      WAYPOINTS_LAYERS.VEGETATION,
      WAYPOINTS_LAYERS.SUNKEN_TREE,
      WAYPOINTS_LAYERS.STRUCTURE,
      WAYPOINTS_LAYERS.SLIPWAY,
      WAYPOINTS_LAYERS.SHELTER,
      WAYPOINTS_LAYERS.SCHOOL_OF_FISH,
      WAYPOINTS_LAYERS.ROCKS,
      WAYPOINTS_LAYERS.PICNIC,
      WAYPOINTS_LAYERS.PHOTO,
      WAYPOINTS_LAYERS.PARKING,
      WAYPOINTS_LAYERS.NOTE,
      WAYPOINTS_LAYERS.NOPE,
      WAYPOINTS_LAYERS.LOST_LURE,
      WAYPOINTS_LAYERS.LOST_FISH,
      WAYPOINTS_LAYERS.LILY_PADS,
      WAYPOINTS_LAYERS.GAS,
      WAYPOINTS_LAYERS.FISH_SPOTTED,
      WAYPOINTS_LAYERS.FAVORITE,
      WAYPOINTS_LAYERS.ELECTRICITY,
      WAYPOINTS_LAYERS.DEFAULT,
      WAYPOINTS_LAYERS.CAMPFIRE,
      WAYPOINTS_LAYERS.ANCHOR_SPOT,
    ],
    sourceName: SOURCE.waypoints.name,
  },
};

export const ORDERED_FISHBRAIN_LAYERS: {
  fishbrainLayer: FishbrainLayer;
  mapboxLayer: MapboxLayer;
}[] = [
  // defines order within this group; highest priority on bottom
  { fishbrainLayer: 'spotPrediction', mapboxLayer: MAPBOX_LAYERS.spotPrediction },
  { fishbrainLayer: 'waypoints', mapboxLayer: MAPBOX_LAYERS.waypoints },
  { fishbrainLayer: 'pointsOfInterest', mapboxLayer: MAPBOX_LAYERS.pointsOfInterest },
  { fishbrainLayer: 'catchPositions', mapboxLayer: MAPBOX_LAYERS.catchPositions },
  { fishbrainLayer: 'artificialReefs', mapboxLayer: MAPBOX_LAYERS.artificialReefs },
  { fishbrainLayer: 'fishingWaters', mapboxLayer: MAPBOX_LAYERS.fishingWaters },
];

// these layers will be always on top of all Fishbrain layers
export const ORDERED_DEFAULT_MAPBOX_LAYERS = [
  // defines order within this group; highest priority on bottom
  'water-label-sm',
  'water-label',
  'poi-parks-scalerank1',
  'poi-scalerank1',
  'place-islets',
  'place-residential',
  'place-neighbourhood',
  'place-suburb',
  'place-hamlet',
  'place-village',
  'place-islands',
  'place-town',
  'place-city-sm',
  'place-city-md-s',
  'place-city-md-n',
  'place-city-lg-s',
  'place-city-lg-n',
  'marine-label-sm-ln',
  'marine-label-sm-pt',
  'marine-label-md-ln',
  'marine-label-md-pt',
  'marine-label-lg-ln',
  'marine-label-lg-pt',
  'state-label-sm',
  'state-label-md',
  'state-label-lg',
  'country-label-sm',
  'country-label-md',
  'country-label-lg',
];
